import { gql, useLazyQuery } from '@apollo/client';
import { DateRangePickerProps } from '@amzn/awsui-components-react';
import moment from 'moment';

interface CloudWatchMetrics {
  avg_cpu: number;
  avg_mem: number;
  avg_rss: number;
  total_shard_cnt: number;
  unique_azs: string[];
  worker_count: number;
}

interface CloudWatchInsightsData {
  timestamp: string;
  metrics: CloudWatchMetrics;
}

export interface JunoCloudWatchInsightsPCVars {
  placementConstraintId: string;
  dataPlaneType: string;
  awsAccountId: string;
  startTime: string;
  endTime: string;
  metrics?: string[];
}

interface JunoCloudWatchInsightsPCResponse {
  getJunoCloudWatchInsightsPC: CloudWatchInsightsData[];
}

const GET_JUNO_CLOUDWATCH_INSIGHTS_PC = gql`
  query GetJunoCloudWatchInsightsPC(
    $placementConstraintId: String!
    $dataPlaneType: String!
    $awsAccountId: String!
    $startTime: String!
    $endTime: String!
    $metrics: [String!]
  ) {
    getJunoCloudWatchInsightsPC(
      placementConstraintId: $placementConstraintId
      dataPlaneType: $dataPlaneType
      awsAccountId: $awsAccountId
      startTime: $startTime
      endTime: $endTime
      metrics: $metrics
    ) {
      timestamp
      metrics {
        avg_cpu
        avg_mem
        avg_rss
        total_shard_cnt
        unique_azs
        worker_count
      }
    }
  }
`;

export const useJunoCloudWatchInsightsPCQuery = () => {
  const [getJunoCloudWatchInsightsPC, queryResult] = useLazyQuery<
    JunoCloudWatchInsightsPCResponse,
    JunoCloudWatchInsightsPCVars
  >(GET_JUNO_CLOUDWATCH_INSIGHTS_PC, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache'
  });

  const fetchData = (
    timeRange: DateRangePickerProps.Value,
    params: {
      placementConstraintId: string;
      dataPlaneType: string;
      awsAccountId: string;
      metrics?: string[];
    }
  ) => {
    let startTime: string;
    let endTime: string;
  
    if (timeRange.type === 'absolute') {
      startTime = moment(timeRange.startDate).format('YYYY-MM-DDTHH:mm:ss');
      endTime = moment(timeRange.endDate).format('YYYY-MM-DDTHH:mm:ss');
    } else {
      const relativeValue = timeRange as DateRangePickerProps.RelativeValue;
      endTime = moment().format('YYYY-MM-DDTHH:mm:ss');
      startTime = moment()
        .subtract(relativeValue.amount, relativeValue.unit)
        .format('YYYY-MM-DDTHH:mm:ss');
    }
  
    return getJunoCloudWatchInsightsPC({
      variables: {
        placementConstraintId: params.placementConstraintId,
        dataPlaneType: params.dataPlaneType,
        awsAccountId: params.awsAccountId,
        startTime,
        endTime,
        metrics: params.metrics || ['RSS', 'CPU', 'MEMORY', 'DISK']
      }
    });
  };

  return {
    ...queryResult,
    fetchData
  };
};

export type { CloudWatchInsightsData, CloudWatchMetrics };